import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import { useEffect, useMemo, useRef, useState } from "react";
import { TeamMealMenuPreviewFragment, useMealMenuSharingSummariesQuery } from "../../../../types";
import AdvancedSelectionDialog from "../../../Tags/Dialogs/Mutation/AdvancedSelection/AdvancedSelectionDialog";
import {
  AdvancedSelectionState,
  buildInitialCreateAdvancedSelectionState,
  buildInitialCreateAdvancedSelectionStateFromTeams,
  buildInitialEditAdvancedSelectionState,
} from "../../../Tags/reducers/advancedSelectionReducers";
import MenusTeamMultiSelect from "../../SelectComponents/TeamMultiSelect";
import { MealMenuInstance } from "../../types";
import { getInitialBulkEditSharingState } from "../../utils";
import {
  formatMenuScheduleLabel,
  formatNewSharingLabel,
  isSharingSummaryEmpty,
  menuSharingIdentityKey,
  sharedAdvancedSelection,
  sharingFormKey,
  summarizeAdvancedSelection,
  summarizeAdvancedSelectionState,
  summarizeTeamNames,
} from "../sharingSummary";
import { BaseBulkEditProps, BaseContentProps } from "../types";
import BaseContent from "./BaseContent";
import CurrentSharing, { CurrentMenuSharing } from "./CurrentSharing";

interface SharingContentProps extends BaseContentProps {
  onBulkEdit: BaseBulkEditProps["onBulkEdit"];
  allTeams: BaseBulkEditProps["allTeams"];
}

const sharingStatusFor = (menu: MealMenuInstance, loading: boolean, failed: boolean): CurrentMenuSharing["status"] => {
  if (!menu.hasAdvancedSelection) {
    return "ready";
  }
  if (failed) {
    return "error";
  }
  if (loading) {
    return "loading";
  }
  return "ready";
};

const SharingContent = ({ isOpen, onBack, onCancel, onBulkEdit, mealMenuInstances, saving, allTeams }: SharingContentProps) => {
  const mealMenuIds = mealMenuInstances.map(mealMenuInstance => mealMenuInstance.id);
  const needsAdvancedSelection = mealMenuInstances.some(menu => menu.hasAdvancedSelection);
  const { data, loading, error } = useMealMenuSharingSummariesQuery({
    variables: { mealMenuIds },
    skip: !needsAdvancedSelection,
  });
  const [initialSelectedTeams, initialAdvancedSelectionState] = getInitialBulkEditSharingState(mealMenuInstances);
  const awaitsAdvancedPrefill = mealMenuInstances.length > 0 && mealMenuInstances.every(menu => menu.hasAdvancedSelection);
  const [selectedTeams, setSelectedTeams] = useState<readonly TeamMealMenuPreviewFragment[]>(initialSelectedTeams ?? []);
  const [advancedSelectionState, setAdvancedSelectionState] = useState<AdvancedSelectionState | null>(initialAdvancedSelectionState);
  const [baselineKey, setBaselineKey] = useState(() => sharingFormKey(initialSelectedTeams ?? [], initialAdvancedSelectionState));
  const [advancedSelectionOpen, setAdvancedSelectionOpen] = useState(false);
  const [advancedPrefillResolved, setAdvancedPrefillResolved] = useState(!awaitsAdvancedPrefill);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [userEditedSharing, setUserEditedSharing] = useState(false);
  const didResolveAdvancedPrefill = useRef(false);

  const advancedSelectionById = useMemo(() => {
    return new Map((data?.mealMenus ?? []).map(menu => [menu.id, menu.advancedSelection]));
  }, [data]);

  const currentMenus = useMemo(() => {
    return [...mealMenuInstances]
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map((menu): CurrentMenuSharing => {
        const status = sharingStatusFor(menu, loading, Boolean(error));
        const advancedSelection = advancedSelectionById.get(menu.id) ?? null;
        const summary =
          menu.hasAdvancedSelection && advancedSelection
            ? summarizeAdvancedSelection(advancedSelection)
            : summarizeTeamNames(menu.hasAdvancedSelection ? [] : menu.teams);
        return {
          id: menu.id,
          name: menu.name,
          mealType: menu.type,
          scheduleLabel: formatMenuScheduleLabel(menu.start, menu.durationInMinutes, menu.timezone),
          summary,
          identityKey: menuSharingIdentityKey(menu.hasAdvancedSelection, menu.teams, advancedSelection),
          status,
        };
      });
  }, [advancedSelectionById, error, loading, mealMenuInstances]);

  useEffect(() => {
    if (didResolveAdvancedPrefill.current || !needsAdvancedSelection || loading) {
      return;
    }
    const finishAdvancedPrefill = () => {
      didResolveAdvancedPrefill.current = true;
      setAdvancedPrefillResolved(true);
    };
    if (userEditedSharing || error || !data || mealMenuInstances.some(menu => !menu.hasAdvancedSelection)) {
      finishAdvancedPrefill();
      return;
    }
    const sharedSelection = sharedAdvancedSelection(mealMenuInstances.map(menu => advancedSelectionById.get(menu.id) ?? null));
    finishAdvancedPrefill();
    if (!sharedSelection) {
      return;
    }
    const nextAdvancedSelection = buildInitialEditAdvancedSelectionState(sharedSelection);
    setSelectedTeams([]);
    setAdvancedSelectionState(nextAdvancedSelection);
    setBaselineKey(sharingFormKey([], nextAdvancedSelection));
  }, [advancedSelectionById, data, error, loading, mealMenuInstances, needsAdvancedSelection, userEditedSharing]);

  const newSummary = advancedSelectionState ? summarizeAdvancedSelectionState(advancedSelectionState) : summarizeTeamNames(selectedTeams);
  const hasNewSharing = !isSharingSummaryEmpty(newSummary);
  // Compare the selection that would be saved. A prefill matches the menus, so it stays hidden until that selection changes.
  const isDirty = sharingFormKey(advancedSelectionState ? [] : selectedTeams, advancedSelectionState) !== baselineKey;
  const displayedSummary = isDirty ? newSummary : summarizeTeamNames([]);
  // Bulk sharing requires teams or an advanced selection. An empty payload is rejected.
  const title = mealMenuInstances.length === 1 ? "Edit sharing" : `Reassign sharing for ${mealMenuInstances.length} meal menus`;

  const submitSharing = async () => {
    if (!hasNewSharing || !isDirty) {
      return;
    }
    await onBulkEdit({
      mealMenuIds,
      actionType: "sharing",
      sharingState: {
        teamIds: advancedSelectionState ? null : selectedTeams.map(team => team.id),
        advancedSelectionState,
      },
      notificationSentBeforeOrderDueInMinutes: null,
      timingState: null,
    });
    onBack();
  };

  return (
    <>
      <BaseContent
        isOpen={isOpen}
        onCancel={onCancel}
        saving={saving}
        onSubmit={() => setConfirmSubmitOpen(true)}
        title={title}
        fullWidth
        renderContent={() => (
          <Stack spacing={2} sx={{ pt: 1, width: "100%", minWidth: 0 }}>
            <Alert
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="warning"
              variant="outlined"
              sx={{
                alignItems: "center",
                color: "text.primary",
                bgcolor: "warning.lighter",
                borderColor: "warning.light",
                "& .MuiAlert-icon": { color: "warning.dark" },
              }}
            >
              Your sharing selection will override any sharing currently in place.
            </Alert>
            <CurrentSharing menus={currentMenus} />
            <Box>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                New sharing
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  border: "1px dashed",
                  borderColor: "grey.400",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color={isDirty && hasNewSharing ? "text.primary" : "text.secondary"}
                  sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {formatNewSharingLabel(displayedSummary)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                  <MenusTeamMultiSelect
                    onChange={teams => {
                      setUserEditedSharing(true);
                      setSelectedTeams(teams);
                      setAdvancedSelectionState(null);
                    }}
                    allTeams={allTeams}
                    selectedTeams={advancedSelectionState ? [] : selectedTeams}
                    sx={{
                      width: 148,
                      m: 0,
                      "& .MuiOutlinedInput-root": { height: 40 },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        color: "text.primary",
                        fontSize: theme => theme.typography.button.fontSize,
                        fontWeight: theme => theme.typography.button.fontWeight,
                        lineHeight: theme => theme.typography.button.lineHeight,
                      },
                    }}
                    props={{
                      size: "small",
                      margin: "none",
                      hiddenLabel: true,
                      label: undefined,
                      SelectProps: {
                        multiple: true,
                        displayEmpty: true,
                        renderValue: () => "Teams",
                        MenuProps: { PaperProps: { sx: { minWidth: 240 } } },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    disabled={!advancedPrefillResolved}
                    onClick={() => {
                      if (advancedPrefillResolved) {
                        setAdvancedSelectionOpen(true);
                      }
                    }}
                    sx={{ height: 40, px: 2, whiteSpace: "nowrap" }}
                  >
                    Advanced selection
                  </Button>
                </Box>
              </Box>
            </Box>
            {advancedSelectionOpen && (
              <AdvancedSelectionDialog
                key={advancedSelectionState?.id ?? "new-advanced-selection"}
                title="Bulk Share Menu"
                open={advancedSelectionOpen}
                onClose={() => setAdvancedSelectionOpen(false)}
                onSave={state => {
                  setUserEditedSharing(true);
                  setAdvancedSelectionOpen(false);
                  if (isSharingSummaryEmpty(summarizeAdvancedSelectionState(state))) {
                    setAdvancedSelectionState(null);
                    setSelectedTeams([]);
                    return;
                  }
                  setSelectedTeams([]);
                  setAdvancedSelectionState(state);
                }}
                initialState={
                  advancedSelectionState ??
                  (selectedTeams.length > 0
                    ? buildInitialCreateAdvancedSelectionStateFromTeams(selectedTeams)
                    : buildInitialCreateAdvancedSelectionState())
                }
              />
            )}
          </Stack>
        )}
        submitDisabled={mealMenuInstances.length === 0 || saving || !isDirty || !hasNewSharing}
      />
      {confirmSubmitOpen && (
        <ConfirmationDialog
          open={confirmSubmitOpen}
          title="Override sharing"
          message="Are you sure you want to override the sharing currently in place?"
          confirmLabel="Submit"
          loading={saving}
          onCancel={() => setConfirmSubmitOpen(false)}
          onConfirm={() => {
            void submitSharing();
          }}
        />
      )}
    </>
  );
};

export default SharingContent;
